import { Navigate, Outlet, RouteObject, useLocation } from 'react-router-dom'
import { ProtectedRoute } from 'lib/rbac'
import { FlaggedRoute } from 'components/QuoteEditor/FlaggedRoute'

export const settingsRoutes = (): RouteObject[] => [
  {
    path: 'settings',
    element: <Outlet />,
    children: [
      {
        path: 'merchant',
        lazy: async () => {
          const { MerchantSettingsPage } = await import(
            './pages/MerchantSettingsPage'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:update">
                <MerchantSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'portal',
        lazy: async () => {
          const { EndCustomerPortalSettingsPage } = await import(
            './pages/EndCustomerPortalSettingsPage'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:update">
                <EndCustomerPortalSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'team',
        lazy: async () => {
          const { TeamSettingsPageV2 } = await import(
            './pages/TeamSettingsPageV2'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:invite">
                <TeamSettingsPageV2 />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'currencies',
        lazy: async () => {
          const { CurrenciesSettingsPage } = await import(
            './pages/CurrenciesSettingsPage'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:update">
                <CurrenciesSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'notifications',
        children: [
          {
            index: true,
            lazy: async () => {
              const { NotificationsSettingsPage } = await import(
                './pages/NotificationsSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <NotificationsSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          },
          {
            path: 'new',
            lazy: async () => {
              const { NotificationsNewPolicySettingsPage } = await import(
                './pages/NotificationsNewPolicySettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <NotificationsNewPolicySettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          },
          {
            path: ':id',
            lazy: async () => {
              const { NotificationsPolicySettingsPage } = await import(
                './pages/NotificationsPolicySettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <NotificationsPolicySettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          }
        ]
      },
      {
        path: 'webhooks',
        children: [
          {
            index: true,
            lazy: async () => {
              const { WebhooksSettingsPage } = await import(
                './pages/WebhooksSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <WebhooksSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          },
          {
            path: 'new',
            lazy: async () => {
              const { WebhooksNewSettingsPage } = await import(
                './pages/WebhooksNewSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <WebhooksNewSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          },
          {
            path: ':id',
            lazy: async () => {
              const { WebhooksEditSettingsPage } = await import(
                './pages/WebhooksEditSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <WebhooksEditSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          }
        ]
      },
      {
        path: 'bank-details',
        children: [
          {
            index: true,
            lazy: async () => {
              const { BankDetailsSettingsPage } = await import(
                './pages/BankDetailsSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <BankDetailsSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          },
          {
            path: 'new',
            lazy: async () => {
              const { BankDetailsNewSettingsPage } = await import(
                './pages/BankDetailsNewSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <BankDetailsNewSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          },
          {
            path: ':currency/:accountType',
            lazy: async () => {
              const { BankDetailsEditSettingsPage } = await import(
                './pages/BankDetailsEditSettingsPage'
              )
              return {
                Component: () => (
                  <ProtectedRoute permission="org.settings:update">
                    <BankDetailsEditSettingsPage />
                  </ProtectedRoute>
                )
              }
            }
          }
        ]
      },
      {
        path: 'invoices',
        lazy: async () => {
          const { InvoicesSettingsPage } = await import(
            './pages/InvoicesSettingsPage'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:update">
                <InvoicesSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'revenue-recognition',
        lazy: async () => {
          const { RevenueRecognitionSettingsPage } = await import(
            './pages/RevenueRecognitionSettingsPage/RevenueRecognitionSettingsPage'
          )
          return {
            Component: () => (
              <FlaggedRoute flag="useRevRec">
                <ProtectedRoute permission="org.revenueRecognition:read">
                  <RevenueRecognitionSettingsPage />
                </ProtectedRoute>
              </FlaggedRoute>
            )
          }
        }
      },
      {
        path: 'api-keys',
        lazy: async () => {
          const { ApiKeysSettingsPage } = await import(
            './pages/ApiKeysSettingsPage'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:update">
                <ApiKeysSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'tax-rates',
        lazy: async () => {
          const { SalesTaxSettingsPage } = await import(
            './pages/SalesTaxSettingsPage'
          )
          return {
            Component: () => (
              <ProtectedRoute permission="org.settings:update">
                <SalesTaxSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      },
      {
        path: 'quotes',
        lazy: async () => {
          const { QuotesSettingsPage } = await import(
            './pages/QuotesSettingsPage'
          )
          return {
            Component: () => (
              <FlaggedRoute flag="showQuoteBuilder">
                <ProtectedRoute permission="org.settings:update">
                  <QuotesSettingsPage />
                </ProtectedRoute>
              </FlaggedRoute>
            )
          }
        }
      },
      {
        path: 'e-signature',
        element: <Navigate to="/settings/quotes" replace />
      },
      {
        path: 'labs',
        lazy: async () => {
          const { LabsSettingsPage } = await import('./pages/LabsSettingsPage')
          return {
            Component: () => (
              // Viewable with read so all roles can discover Labs; the
              // toggles themselves are gated on org.settings:update so only
              // admins can enable features.
              <ProtectedRoute permission="org.settings:read">
                <LabsSettingsPage />
              </ProtectedRoute>
            )
          }
        }
      }
    ]
  }
]

export const useIsOnSettingsPage = () => {
  const { pathname } = useLocation()
  return pathname.startsWith('/settings/')
}
